import { cn } from '@/lib/utils'
import { DriverActivityType } from '@/modules/activities/consts'
import { format } from '@/utils/date-fns'
import { useActivityColor } from '@/utils/hooks'
import { useTranslate } from '@ur-solutions/react-hooks'
import { isSameDay } from 'date-fns'
import { useRef, useState } from 'react'
import type { ActivitySpan, PauseDragMode, TimeSpan } from '../time-entry-pause'
import {
  clipActivitiesToSpan,
  dragPause,
  formatSecondsAsDuration,
  getPauseBounds,
  getSpanSeconds,
  isPaidPause,
  sortSpans,
  splitAroundPauses,
} from '../time-entry-pause'

type Segment = TimeSpan &
  ({ kind: 'work'; entryNumber: number } | { kind: 'pause'; index: number })

interface Props {
  span: TimeSpan
  pauses: TimeSpan[]
  paidPauseThreshold?: number | null
  activities?: ActivitySpan[]
  /** Whether the activity lookup finished; an empty result is then shown as such. */
  activitiesLoaded?: boolean
  onPauseChange?(index: number, pause: TimeSpan): void
}

const KEYBOARD_STEP_MS = 5 * 60 * 1000

export const TimeEntryPreview: React.FC<Props> = ({
  span,
  pauses,
  paidPauseThreshold = null,
  activities = [],
  activitiesLoaded = false,
  onPauseChange,
}) => {
  const translations = useTranslate({
    title: 'timesheets.time-entry-form.preview.title',
    entry: ['timesheets.time-entry-form.preview.entry', { n: 0 }],
    workTotal: [
      'timesheets.time-entry-form.preview.work-total',
      { duration: '' },
    ],
    dragHint: 'timesheets.time-entry-form.preview.drag-hint',
    activities: 'timesheets.time-entry-form.preview.activities',
    noActivities: 'timesheets.time-entry-form.preview.no-activities',
    paidPause: 'timesheets.time-entry-form.preview.paid-pause',
    unpaidPause: 'timesheets.time-entry-form.preview.unpaid-pause',
    pause: 'common.pause',
  })

  const getActivityColor = useActivityColor()
  const barRef = useRef<HTMLDivElement>(null)
  const [dragMode, setDragMode] = useState<PauseDragMode | null>(null)

  const totalMs = span.end.getTime() - span.start.getTime()
  if (totalMs <= 0) return null

  const entries = splitAroundPauses(span, pauses)
  const sortedPauses = sortSpans(
    pauses.map((pause, index) => ({ ...pause, index }))
  )
  const segments = entries.flatMap<Segment>((entry, idx) => {
    const work: Segment = { ...entry, kind: 'work', entryNumber: idx + 1 }
    const pause = sortedPauses[idx]
    return pause ? [work, { ...pause, kind: 'pause' }] : [work]
  })

  const draggable = !!onPauseChange
  const workColor = getActivityColor({ activityType: DriverActivityType.WORK })
  const visibleActivities = clipActivitiesToSpan(
    activities,
    span,
    paidPauseThreshold
  )

  function pauseStyle(pause: TimeSpan) {
    const paid = isPaidPause(pause, paidPauseThreshold)
    return {
      color: getActivityColor({
        activityType: paid
          ? DriverActivityType.PAID_REST
          : DriverActivityType.REST,
      }),
      label: !paidPauseThreshold
        ? translations.pause
        : paid
        ? translations.paidPause
        : translations.unpaidPause,
    }
  }

  function position(segment: TimeSpan) {
    return {
      left: ((segment.start.getTime() - span.start.getTime()) / totalMs) * 100,
      width:
        ((segment.end.getTime() - segment.start.getTime()) / totalMs) * 100,
    }
  }

  function formatTime(date: Date) {
    const clock = format(date, 'HH:mm')
    return isSameDay(date, span.start) ? clock : `${clock} (+1)`
  }

  function handlePointerDown(mode: PauseDragMode, index: number) {
    return (evt: React.PointerEvent<HTMLElement>) => {
      const pause = pauses[index]
      if (!pause || !onPauseChange || !barRef.current) return
      const origin: TimeSpan = pause
      evt.preventDefault()
      evt.stopPropagation()

      const originX = evt.clientX
      const barWidth = barRef.current.getBoundingClientRect().width
      const bounds = getPauseBounds(span, pauses, index)
      let last = origin

      function handleMove(moveEvt: PointerEvent) {
        if (!onPauseChange || barWidth === 0) return

        const deltaMs = ((moveEvt.clientX - originX) / barWidth) * totalMs
        const next = dragPause(origin, bounds, mode, deltaMs)
        if (
          next.start.getTime() === last.start.getTime() &&
          next.end.getTime() === last.end.getTime()
        )
          return

        last = next
        onPauseChange(index, next)
      }

      function handleEnd() {
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleEnd)
        window.removeEventListener('pointercancel', handleEnd)
        setDragMode(null)
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleEnd)
      window.addEventListener('pointercancel', handleEnd)
      setDragMode(mode)
    }
  }

  function handleKeyDown(index: number) {
    return (evt: React.KeyboardEvent<HTMLElement>) => {
      const pause = pauses[index]
      if (!pause || !onPauseChange) return
      if (evt.key !== 'ArrowLeft' && evt.key !== 'ArrowRight') return
      evt.preventDefault()

      const direction = evt.key === 'ArrowLeft' ? -1 : 1
      onPauseChange(
        index,
        dragPause(
          pause,
          getPauseBounds(span, pauses, index),
          'move',
          direction * KEYBOARD_STEP_MS
        )
      )
    }
  }

  return (
    <div className="space-y-3 rounded-xl border border-stone-200 bg-stone-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span className="text-xs font-semibold uppercase text-stone-500">
          {translations.title}
        </span>

        <span className="text-xs font-medium text-stone-600 tabular-nums">
          {translations.workTotal({
            duration: formatSecondsAsDuration(getSpanSeconds(entries)),
          })}
        </span>
      </div>

      <div className="space-y-1.5">
        {(activitiesLoaded || visibleActivities.length > 0) && (
          <div className="space-y-1">
            <span className="block text-[11px] font-medium text-stone-500">
              {visibleActivities.length > 0
                ? translations.activities
                : translations.noActivities}
            </span>

            <div className="relative h-3 w-full overflow-hidden rounded-full bg-stone-200">
              {visibleActivities.map(activity => {
                const { left, width } = position(activity)
                return (
                  <div
                    key={`${activity.start.toISOString()}-${
                      activity.activityType
                    }`}
                    className="absolute inset-y-0"
                    style={{
                      left: `${left}%`,
                      width: `${width}%`,
                      backgroundColor: getActivityColor(activity),
                    }}
                    title={`${activity.activityType}: ${formatTime(
                      activity.start
                    )} – ${formatTime(activity.end)}`}
                  />
                )
              })}
            </div>
          </div>
        )}

        <div
          ref={barRef}
          className={cn(
            'relative h-12 w-full overflow-hidden rounded-lg select-none',
            dragMode === 'move' && 'cursor-grabbing',
            (dragMode === 'start' || dragMode === 'end') && 'cursor-ew-resize'
          )}
        >
          {segments.map(segment => {
            const { left, width } = position(segment)
            const seconds = getSpanSeconds([segment])
            const showLabel = width >= 15
            const time = `${formatTime(segment.start)} – ${formatTime(
              segment.end
            )}`

            if (segment.kind === 'work')
              return (
                <div
                  key={`work-${segment.entryNumber}`}
                  className="@container absolute inset-y-0 flex items-center justify-center overflow-hidden text-xs font-semibold whitespace-nowrap text-primary tabular-nums"
                  style={{
                    left: `${left}%`,
                    width: `${width}%`,
                    backgroundColor: workColor,
                  }}
                  title={time}
                >
                  <span className="hidden @min-[6rem]:inline md:@min-[4rem]:inline">
                    {formatSecondsAsDuration(seconds)}
                  </span>
                </div>
              )

            const { color, label } = pauseStyle(segment)

            return (
              <div
                key={`pause-${segment.index}`}
                role={draggable ? 'slider' : undefined}
                tabIndex={draggable ? 0 : undefined}
                aria-label={label}
                aria-valuetext={time}
                title={`${label}: ${time}`}
                className={cn(
                  'absolute inset-y-0 z-10 flex items-center justify-center text-xs font-semibold whitespace-nowrap text-white ring-2 ring-stone-50',
                  draggable &&
                    'touch-none outline-none focus-visible:ring-secondary'
                )}
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor: color,
                }}
                onKeyDown={handleKeyDown(segment.index)}
              >
                {showLabel && (
                  <span className="pointer-events-none">
                    {translations.pause}
                  </span>
                )}

                {draggable && (
                  <>
                    {/* Wider than a short pause so it stays easy to grab. */}
                    <span
                      className={cn(
                        'absolute inset-y-0 left-1/2 w-[max(100%,4rem)] -translate-x-1/2 touch-none md:w-[max(100%,2.5rem)]',
                        !dragMode && 'cursor-grab'
                      )}
                      onPointerDown={handlePointerDown('move', segment.index)}
                    />

                    {(['start', 'end'] as const).map(edge => (
                      <span
                        key={edge}
                        className={cn(
                          'absolute inset-y-0 z-10 flex w-6 cursor-ew-resize touch-none items-center justify-center md:w-4',
                          edge === 'start'
                            ? 'left-0 -translate-x-1/2'
                            : 'right-0 translate-x-1/2'
                        )}
                        onPointerDown={handlePointerDown(edge, segment.index)}
                      >
                        <span className="h-6 w-1 rounded-full bg-white shadow-sm ring-1 ring-stone-400/60" />
                      </span>
                    ))}
                  </>
                )}
              </div>
            )
          })}
        </div>

        <div className="flex justify-between text-[11px] font-medium text-stone-500 tabular-nums">
          <span>{formatTime(span.start)}</span>
          <span>{formatTime(span.end)}</span>
        </div>

        {draggable && pauses.length > 0 && (
          <p className="text-[11px] text-stone-500">{translations.dragHint}</p>
        )}
      </div>

      {entries.length > 1 && (
        <ul className="divide-y divide-stone-200 rounded-lg bg-white text-sm">
          {segments.map(segment => {
            const pause = segment.kind === 'pause' ? pauseStyle(segment) : null

            return (
              <li
                key={
                  segment.kind === 'work'
                    ? `work-${segment.entryNumber}`
                    : `pause-${segment.index}`
                }
                className="flex items-center gap-3 px-3 py-2"
              >
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: pause?.color ?? workColor }}
                />

                <span
                  className={cn(
                    'min-w-0 flex-1 truncate font-medium',
                    pause ? 'text-stone-500' : 'text-stone-900'
                  )}
                >
                  {segment.kind === 'work'
                    ? translations.entry({ n: segment.entryNumber })
                    : pause?.label}
                </span>

                <span className="whitespace-nowrap text-stone-600 tabular-nums">
                  {formatTime(segment.start)} – {formatTime(segment.end)}
                </span>

                <span className="w-10 text-right font-medium text-stone-900 tabular-nums">
                  {formatSecondsAsDuration(getSpanSeconds([segment]))}
                </span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
